/**
 * Single source of truth for turning a Sharetribe listing entity into the
 * props consumed by `MarketplaceListingCard`. Used by SearchPage,
 * LandingPage's featured/reduced sections, ProfilePage's listings grid:
 * anywhere a listing needs to render through the same Tailwind card.
 */
import { createSlug } from './urlHelpers';
import { formatPublicLocation } from './listingLocationDisplay';
import { formatPostedRelative } from './dates';
import { getUserMembership } from '../components/SellerBadges/SellerBadges';

const CONDITION_LABELS: Record<string, string> = {
  leftover: 'New / Leftover',
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  non_working: 'Non-Working',
};

export type ListingCardAdapterOptions = {
  categories?: Array<{ id: string; name: string }>;
  /**
   * Override the seller used for membership/verified badges. Profile pages
   * already know who the seller is and may not include `author` on each listing.
   */
  sellerOverride?: any;
};

export type ListingCardProps = {
  id: string;
  slug: string;
  title: string;
  imgUrl: string;
  preloadSrc?: string;
  priceLabel: string;
  oldPriceLabel?: string;
  condition: string;
  location: string;
  category: string;
  posted: string;
  reviewAvg?: number;
  reviewCount?: number;
  membershipTier: number;
  isVerified: boolean;
};

const formatPriceCents = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
  }).format(amount / 100);

/**
 * Adapt a Sharetribe listing entity into the prop shape expected by
 * `MarketplaceListingCard`. Callers must ensure their SDK queries include
 * `author` (and `'fields.user': ['profile.metadata']`) when they want
 * tier/verified badges to populate.
 *
 * @param listing - Sharetribe listing entity (with attributes + images)
 * @param options - `categories` for the display label; `sellerOverride` to
 *   force a specific user (e.g. the profile owner on a profile page).
 */
export const mapListingToCardProps = (
  listing: any,
  options: ListingCardAdapterOptions = {}
): ListingCardProps => {
  const { categories = [], sellerOverride } = options;
  const id = listing?.id?.uuid;
  const attrs = listing?.attributes || {};
  const { title = '', price, publicData = {}, createdAt, metadata = {} } = attrs;
  const slug = createSlug(title);

  const firstImage = listing?.images?.[0];
  const imgVariants = firstImage?.attributes?.variants || {};
  const imgUrl =
    imgVariants['listing-card-2x']?.url || imgVariants['listing-card']?.url || '';
  const preloadSrc =
    imgVariants['scaled-medium']?.url || imgVariants['scaled-small']?.url || undefined;

  const { amount, currency } = price || {};
  const priceLabel = amount != null ? formatPriceCents(amount, currency) : '–';

  // Optional pre-discount price for "Recently Reduced" cards. Operators set
  // `publicData.oldPrice` (in cents) when running a price drop.
  const oldPriceCents = publicData?.oldPrice;
  const oldPriceLabel =
    typeof oldPriceCents === 'number' ? formatPriceCents(oldPriceCents, currency) : undefined;

  const conditionKey = publicData?.condition || '';
  const conditionDisplay = CONDITION_LABELS[conditionKey] || conditionKey;
  const locationDisplay = formatPublicLocation(publicData?.location);
  const posted = formatPostedRelative(createdAt);

  const categoryId = publicData?.categoryLevel1 || '';
  const cat = categories.find(c => c.id === categoryId);
  const categoryDisplay = cat?.name || categoryId;

  const seller = sellerOverride || listing?.author;
  const { tier: membershipTier, isVerified } = getUserMembership(seller);

  return {
    id,
    slug,
    title,
    imgUrl,
    preloadSrc,
    priceLabel,
    oldPriceLabel,
    condition: conditionDisplay,
    location: locationDisplay,
    category: categoryDisplay,
    posted,
    reviewAvg: metadata?.reviewAvg ?? undefined,
    reviewCount: metadata?.reviewCount ?? undefined,
    membershipTier,
    isVerified,
  };
};
